import { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ErrorBoundary from "@/components/ErrorBoundary";
import Seo from "@/components/Seo";
interface RecEmbed {
  load: (config: Record<string, unknown>) => void;
}
const TrabalheConosco = () => {
  useEffect(() => {
    const loadZohoEmbed = async () => {
      try {
        const cssId = "zoho-embed-jobs-css";
        if (!document.getElementById(cssId)) {
          const link = document.createElement("link");
          link.id = cssId;
          link.rel = "stylesheet";
          link.href = "https://static.zohocdn.com/recruit/embed_careers_site/css/v1.1/embed_jobs.css";
          link.onerror = () => console.warn("Failed to load Zoho CSS");
          document.head.appendChild(link);
        }

        const showFallback = () => {
          const fallbackDiv = document.getElementById("rec_job_listing_div");
          if (fallbackDiv) {
            fallbackDiv.innerHTML = `
              <div class="text-center p-8 bg-muted/30 rounded-lg">
                <p class="text-muted-foreground">
                  Sistema de vagas temporariamente indisponível.
                  Entre em contato conosco pelo email: talentos@quimical.com.br
                </p>
              </div>
            `;
          }
        };

        const script = document.createElement("script");
        script.src = "https://static.zohocdn.com/recruit/embed_careers_site/javascript/v1.1/embed_jobs.js";
        script.defer = true;

        // Fallback in case the widget never finishes loading
        const container = document.getElementById("rec_job_listing_div");
        const timeoutId = window.setTimeout(showFallback, 10000);
        let observer: MutationObserver | undefined;
        if (container) {
          observer = new MutationObserver(() => {
            if (!container.innerHTML.includes("loading-skeleton")) {
              window.clearTimeout(timeoutId);
              observer?.disconnect();
            }
          });
          observer.observe(container, { childList: true });
        }

        script.onload = () => {
          try {
            const recEmbed = (window as unknown as {
              rec_embed_js: RecEmbed;
            }).rec_embed_js;
            if (recEmbed) {
              recEmbed.load({
                widget_id: "rec_job_listing_div",
                page_name: "Careers",
                source: "CareerSite",
                site: "https://talentos.quimical.com.br",
                brand_color: "#6875E2",
                empty_job_msg: "No current Openings",
              });
            }
          } catch (error) {
            console.warn("Error initializing Zoho embed:", error);
            showFallback();
          }
        };

        script.onerror = () => {
          console.warn("Failed to load Zoho script");
          showFallback();
        };

        document.body.appendChild(script);
        return () => {
          try {
            document.body.removeChild(script);
          } catch {
            /* ignore */
          }
          window.clearTimeout(timeoutId);
          observer?.disconnect();
        };
      } catch (error) {
        console.warn("Error setting up Zoho embed:", error);
      }
    };
    const cleanupPromise = loadZohoEmbed();
    return () => {
      cleanupPromise.then((cleanup) => cleanup && cleanup());
    };
  }, []);
  return <div className="min-h-screen bg-background">
      <Seo
        title="Trabalhe na Quimical: vagas e cultura"
        description="Confira vagas abertas e conheça a cultura da Quimical. Candidate-se e faça parte da equipe."
        canonical="https://quimical.com.br/trabalheconosco"
      />
      <ErrorBoundary componentName="Header">
        <Header />
      </ErrorBoundary>
      <main className="container mx-auto px-4 py-16">
        <h1 className="text-4xl font-bold mb-4">Trabalhe Conosco</h1>
        <p className="text-lg mb-8">
          Na Quimical, o <strong>espírito de equipe</strong> está no centro de tudo o que fazemos.
          Valorizamos colaboração, respeito e o desejo constante de crescer juntos.
          Nossos valores de Transparência, Agregar valor, Alta performance, Renovação,
          Responsabilidade, Sustentabilidade, "Ser" humano e Visão de dono orientam
          cada decisão, construindo um ambiente onde todos podem se desenvolver.
        </p>

        <p className="text-lg mb-8">
          Reforçamos diariamente uma cultura de confiança, colaboração e cuidado
          com as pessoas. Aqui, cada colaborador encontra espaço para aprender,
          inovar e prosperar.
        </p>

        <p className="text-lg mb-8">
          Com forte presença no mercado químico, a <strong>Quimical</strong> é reconhecida
          pela qualidade e pela inovação, oferecendo soluções que impulsionam
          nossos clientes e desenvolvem a indústria brasileira. Junte-se a uma
          equipe que está à frente do setor e construa sua carreira conosco.
        </p>

        <ErrorBoundary componentName="ZohoJobsWidget" fallback={<div className="text-center p-8 bg-muted/30 rounded-lg">
              <h3 className="font-semibold mb-4">Vagas Disponíveis</h3>
              <p className="text-muted-foreground mb-4">
                Sistema de vagas temporariamente indisponível. 
              </p>
              <p className="text-sm text-muted-foreground">
                Entre em contato conosco pelo email: <br />
                <a
                  href="mailto:talentos@quimical.com.br"
                  className="text-primary hover:underline"
                >
                  talentos@quimical.com.br
                </a>
              </p>
            </div>}>
          <div className="mb-8">
            <div id="rec_job_listing_div" className="min-h-[400px]">
              <div className="text-center p-8">
                <div className="loading-skeleton w-full h-32 rounded mb-4"></div>
                <div className="loading-skeleton w-3/4 h-4 rounded mx-auto mb-2"></div>
                <div className="loading-skeleton w-1/2 h-4 rounded mx-auto"></div>
              </div>
            </div>
          </div>
        </ErrorBoundary>

        <p className="text-lg mb-8">
          Caso queira enviar seu currículo, encaminhe para
          {" "}
          <a
            href="mailto:talentos@quimical.com.br"
            className="text-primary hover:underline"
          >
            talentos@quimical.com.br
          </a>
          .
        </p>

        <div className="mt-12 grid md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold">Nossos Valores</h2>
            <ul className="space-y-2 text-muted-foreground">
              <li>• <strong>Transparência:</strong> Comunicação clara e honesta</li>
              <li>• <strong>Agregar valor:</strong> Entregar soluções que fazem a diferença</li>
              <li>• <strong>Alta performance:</strong> Busca contínua pela excelência</li>
              <li>• <strong>Renovação:</strong> Inovação e melhoria contínua</li>
              <li>• <strong>Responsabilidade:</strong> Comprometimento com resultados</li>
              <li>• <strong>Sustentabilidade:</strong> Respeito ao meio ambiente e à sociedade</li>
              <li>• <strong>"Ser" humano:</strong> Valorização das pessoas em primeiro lugar</li>
              <li>• <strong>Visão de Dono:</strong> Cuidado e dedicação em tudo</li>
            </ul>
          </div>
          
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold">Benefícios</h2>
            <ul className="space-y-2 text-muted-foreground">
              <li>• Remuneração acima da média</li>
              <li>• Plano de saúde</li>
              <li>• Plano odontológico</li>
              <li>• Seguro de vida</li>
              <li>• Vale alimentação</li>
              <li>• Bonificações</li>
            </ul>
          </div>
        </div>
      </main>
      <ErrorBoundary componentName="Footer">
        <Footer />
      </ErrorBoundary>
    </div>;
};
export default TrabalheConosco;
